import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import DialogWithDontAskAgainActions from 'components/ui/DialogWithDontAskAgainActions';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import Textarea from 'components/ui/Textarea';

type ReopenBatchDialogProps = {
    open: boolean;
    batchId?: number;
    message: string;
    /** What the comment starts as, e.g. the package that turned up. */
    defaultComment?: string;
    busy?: boolean;
    onCancel: () => void;
    onConfirm: (comment: string) => void;
};

/** Asks why a completed batch is reopened: the reason is kept on the batch. */
const ReopenBatchDialog = ({ open, onCancel, ...form }: ReopenBatchDialogProps) => (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
        {/* Mounted only while open, so every opening starts from the default comment. */}
        {open && <ReopenBatchForm onCancel={onCancel} {...form} />}
    </Dialog>
);

const ReopenBatchForm = ({
    batchId,
    message,
    defaultComment = '',
    busy = false,
    onCancel,
    onConfirm,
}: Omit<ReopenBatchDialogProps, 'open'>) => {
    const { t } = useTranslation();
    const [comment, setComment] = useState(defaultComment);
    const canConfirm = comment.trim().length > 0 && !busy;

    return (
        <>
            <DialogTitle>{t('batchReturn.reopenTitle', 'Reopen batch #{{id}}', { id: batchId })}</DialogTitle>
            <DialogContent>
                <Flex column gap="0.75rem">
                    <Text>{message}</Text>
                    <Flex column gap="0.25rem">
                        <Text fontSize={12} bold>
                            {t('batchReturn.reopenComment', 'Why is the batch reopened?')}
                        </Text>
                        <Textarea
                            rows={3}
                            maxLength={1000}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            autoFocus
                        />
                    </Flex>
                </Flex>
            </DialogContent>
            <DialogActions>
                <DialogWithDontAskAgainActions
                    onCancel={onCancel}
                    onConfirm={() => onConfirm(comment.trim())}
                    confirmLabel={t('batchReturn.reopenBatch', 'Reopen batch')}
                    confirmDisabled={!canConfirm}
                />
            </DialogActions>
        </>
    );
};

export default ReopenBatchDialog;
